import { useTranslation } from '../../contexts/LanguageContext'
import type { CVSettings, DateFormat } from '../../types/cv'

interface SettingsFormProps {
  data: CVSettings | undefined
  onChange: (settings: Partial<CVSettings>) => void
}

export function SettingsForm({ data, onChange }: SettingsFormProps) {
  const { t } = useTranslation()
  const currentFormat = data?.dateFormat || 'month-year'
  const showContactIcons = data?.showContactIcons ?? true

  const dateFormatOptions: { value: DateFormat; label: string; example: string }[] = [
    { value: 'month-year', label: t.settingsForm.monthYear, example: 'ene 2024' },
    { value: 'year-only', label: t.settingsForm.yearOnly, example: '2024' },
  ]

  return (
    <div className="space-y-6">
      {/* Contact Icons Toggle */}
      <div>
        <label className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors">
          <div className="flex-1">
            <span className="text-sm font-medium text-slate-900 block">
              {t.settingsForm.contactIcons}
            </span>
            <span className="text-xs text-slate-500">
              {t.settingsForm.contactIconsDesc}
            </span>
          </div>
          <div className="relative ml-3">
            <input
              type="checkbox"
              checked={showContactIcons}
              onChange={(e) => onChange({ showContactIcons: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </div>
        </label>
      </div>

      {/* Date Format */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {t.settingsForm.dateFormat}
        </label>
        <p className="text-xs text-slate-500 mb-3">
          {t.settingsForm.dateFormatDesc}
        </p>
        <div className="space-y-2">
          {dateFormatOptions.map((option) => (
            <label
              key={option.value}
              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                currentFormat === option.value
                  ? 'border-primary bg-primary/5'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  name="dateFormat"
                  value={option.value}
                  checked={currentFormat === option.value}
                  onChange={() => onChange({ dateFormat: option.value })}
                  className="w-4 h-4 text-primary border-slate-300 focus:ring-primary"
                />
                <span className="text-sm font-medium text-slate-900">
                  {option.label}
                </span>
              </div>
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                {option.example}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}
